import { Card, CardContent } from '../../components/ui/card';
import { Button, buttonVariants } from '../../components/ui/button';
import { Paperclip, Download, Trash2 } from 'lucide-react';
import { ConcessionDocument } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import FileDropZone from '../../components/common/FileDropZone';
import FileDownloadLink from '../../components/common/FileDownloadLink';

interface AttachmentsTabProps {
  documents: ConcessionDocument[];
  uploading: boolean;
  currentUserId?: number;
  onUpload: (file: File) => void;
  onDelete: (documentId: number) => void;
}

/** Render a byte count the way a person would say it. */
function formatFileSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * CR-6510: evidence for a concession — both sides.
 *
 * A concession is an argument for accepting out-of-spec parts, and the
 * evidence is the substance of it: the supplier's measurement report or a
 * photo of the defect, and on the customer's side their own signed
 * concession document returned with the decision. Both were asked for and
 * neither existed.
 */
export function AttachmentsTab({
  documents,
  uploading,
  currentUserId,
  onUpload,
  onDelete,
}: AttachmentsTabProps) {
  const { formatDateTime } = useFormatters();

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        {/* Customer review 11 Sep (drag and drop everywhere). */}
        <FileDropZone
          busy={uploading}
          hint="A measurement report, a photo of the defect, or your own concession document. Visible to both the supplier and the customer."
          onFiles={(files) => onUpload(files[0])}
          data-testid="concession-attachments-drop-zone"
        />

        <div className="space-y-2 border-t pt-4">
          {documents.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No attachments yet
            </p>
          ) : (
            documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/50"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{doc.file_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {doc.uploaded_by_name || 'Unknown'}
                      {' · '}
                      {formatDateTime(doc.uploaded_at)}
                      {doc.file_size ? ` · ${formatFileSize(doc.file_size)}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {/* Rows uploaded before the file field existed have no
                      bytes behind them, so there is nothing to download. */}
                  {doc.download_url && (
                    /* CR-7482: fetched through the API client, not linked. A
                       plain anchor to the download endpoint sends no auth
                       token, so the browser landed on a DRF 401 page. */
                    <FileDownloadLink
                      url={doc.download_url}
                      fallbackName={doc.file_name}
                      icon={<Download className="h-4 w-4" />}
                      aria-label={`Download ${doc.file_name}`}
                      className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                    />
                  )}
                  {/* Deleting someone else's evidence would let either side
                      quietly remove the basis of a decision, so only the
                      uploader can withdraw their own. */}
                  {currentUserId === doc.uploaded_by && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(doc.id)}
                      aria-label={`Delete ${doc.file_name}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
